import { z } from 'zod';
import { RegionLabelTransformer } from '../transformers/region.transformers';
import { LanguageLabelTransformer } from '../transformers/language.transformers';

const FacetSubitemSchema = z.object({
    value: z.string(),
    label: z.string(),
});

export const FacetValueSchema = z.object({
    value: z.string(),
    label: z.string(),
    logo: z.string().optional(),
    subitems: z.array(FacetSubitemSchema).optional(),
});

export type FacetValue = z.infer<typeof FacetValueSchema>;
export type FacetSubitem = z.infer<typeof FacetSubitemSchema>;

const ExpertTypeFacetSchema = z.array(FacetValueSchema);
const ServicesOfferedFacetSchema = z.array(FacetValueSchema);

// Country facet with label transformation (ISO2 code -> country name)
const CountriesFacetSchema = z.array(
    z.object({
        value: z.string(),
        label: z.string().transform((val) => {
            try {
                return RegionLabelTransformer(val);
            } catch {
                return val; // Fallback to original if transformation fails
            }
        }),
    })
);

// Language facet with label transformation (code -> language name)
const LanguagesFacetSchema = z.array(
    z.object({
        value: z.string(),
        label: z.string().transform((val) => {
            try {
                return LanguageLabelTransformer(val);
            } catch {
                return val; // Fallback to original if transformation fails
            }
        }),
    })
);

export const CityFacetValueSchema = z.object({
    value: z.number().int(),
    label: z.string(),
    region: z.string().nullable().optional(),
    country: z.string(),
});

export type CityFacetValue = z.infer<typeof CityFacetValueSchema>;

const CitiesFacetSchema = z.array(CityFacetValueSchema);

const AppsAndThemesFacetSchema = z.array(FacetValueSchema);
const PartnerTiersFacetSchema = z.array(FacetValueSchema);
const StartingPriceFacetSchema = z.array(FacetValueSchema);

export const FacetsResponseSchema = z.object({
    expert_type: ExpertTypeFacetSchema,
    services_offered: ServicesOfferedFacetSchema,
    countries: CountriesFacetSchema,
    cities: CitiesFacetSchema.optional().default([]),
    languages: LanguagesFacetSchema,
    apps_and_themes: AppsAndThemesFacetSchema,
    partner_tiers: PartnerTiersFacetSchema,
    starting_price: StartingPriceFacetSchema.optional(),
});

export type FacetsResponse = z.infer<typeof FacetsResponseSchema>;

